import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { scansApi, patientsApi, generationApi, getFileUrl } from '../../lib/api';
import { getToken } from '../../lib/auth';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Baby,
  Loader2,
  Sparkles,
  Ruler,
  User,
  Save,
  Eye
} from 'lucide-react';

// Varsayılan ölçü değerleri
const DEFAULT_MEASUREMENTS = {
  fromen: 30,
  burun: 8,
  goztepe: 20,
  bioc_cap: 45,
  cene: 25,
  agiz_capi: 15,
  onarka_bas: 90,
  bpd: 50,
  hc: 180,
  goz: 12,
  ust_dudak: 8,
  gender: 'female'
};

// Ölçü alanları konfigürasyonu
const MEASUREMENT_FIELDS = [
  { key: 'bpd', label: 'BPD', unit: 'mm', description: 'Biparietal Çap', min: 20, max: 100 },
  { key: 'hc', label: 'HC', unit: 'mm', description: 'Baş Çevresi', min: 100, max: 400 },
  { key: 'fromen', label: 'Fromen', unit: 'mm', description: 'Alın Yüksekliği', min: 15, max: 50 },
  { key: 'burun', label: 'Burun', unit: 'mm', description: 'Burun Uzunluğu', min: 3, max: 20 },
  { key: 'goztepe', label: 'Göztepe', unit: 'mm', description: 'Göz Arası Mesafe', min: 10, max: 40 },
  { key: 'bioc_cap', label: 'BiocÇap', unit: 'mm', description: 'Biorbital Çap', min: 25, max: 70 },
  { key: 'cene', label: 'Çene', unit: 'mm', description: 'Çene Uzunluğu', min: 10, max: 50 },
  { key: 'agiz_capi', label: 'Ağız Çapı', unit: 'mm', description: 'Ağız Genişliği', min: 5, max: 30 },
  { key: 'onarka_bas', label: 'Önarka Baş', unit: 'mm', description: 'Ön-Arka Baş Çapı', min: 50, max: 150 },
  { key: 'goz', label: 'Göz', unit: 'mm', description: 'Göz Boyutu', min: 5, max: 25 },
  { key: 'ust_dudak', label: 'Üst Dudak', unit: 'mm', description: 'Üst Dudak Uzunluğu', min: 3, max: 20 },
];

export const BiometrikPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const scanId = searchParams.get('scanId');

  const [scan, setScan] = useState(null);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState(null);

  // Ölçümler form state
  const [measurements, setMeasurements] = useState({ ...DEFAULT_MEASUREMENTS });

  // Fetch generated image with auth
  const fetchGeneratedImage = useCallback(async (imageUrl) => {
    if (!imageUrl) return;
    try {
      const token = getToken();
      const fullUrl = getFileUrl(imageUrl);
      const response = await fetch(fullUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        setGeneratedImageUrl(URL.createObjectURL(blob));
      }
    } catch (error) {
      console.error('Failed to fetch generated image:', error);
    }
  }, []);

  const fetchData = useCallback(async () => {
    if (!scanId) {
      toast.error('Tarama ID bulunamadı');
      navigate('/doctor/patients');
      return;
    }

    try {
      setLoading(true);
      
      // Get scan data
      const scanData = await scansApi.get(scanId);
      setScan(scanData);
      
      // Get patient data
      const patientData = await patientsApi.get(scanData.patient_id);
      setPatient(patientData);

      // Load existing measurements if any
      if (scanData.metadata?.measurements) {
        setMeasurements(prev => ({ ...prev, ...scanData.metadata.measurements }));
      }

      // Load generated image if exists
      if (scanData.baby_face_url) {
        fetchGeneratedImage(scanData.baby_face_url);
      }
    } catch (error) {
      toast.error('Veri yüklenirken hata oluştu');
      navigate('/doctor/patients');
    } finally {
      setLoading(false);
    }
  }, [scanId, navigate, fetchGeneratedImage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Cleanup blob URL
  useEffect(() => {
    return () => {
      if (generatedImageUrl) {
        URL.revokeObjectURL(generatedImageUrl);
      }
    };
  }, [generatedImageUrl]);

  const handleMeasurementChange = (key, value) => {
    setMeasurements(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSaveMeasurements = async () => {
    setSaving(true);
    try {
      await scansApi.update(scanId, {
        metadata: { measurements }
      });
      toast.success('Ölçümler kaydedildi');
    } catch (error) {
      toast.error('Kaydetme hatası');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      // Save measurements first
      await scansApi.update(scanId, {
        metadata: { measurements },
        input_type: 'biometric'
      });

      // Generate fetal face
      const result = await generationApi.generate(scanId, 'fetal');
      
      toast.success(result.message || 'Görsel üretildi!');
      
      // Reload scan data
      const updatedScan = await scansApi.get(scanId);
      setScan(updatedScan);
      
      if (updatedScan.baby_face_url) {
        fetchGeneratedImage(updatedScan.baby_face_url);
      }
    } catch (error) {
      toast.error(error.message || 'Üretim hatası');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Biyometrik Analiz">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-[#5A67D8]" />
        </div>
      </DashboardLayout>
    );
  }

  if (!scan || !patient) {
    return (
      <DashboardLayout title="Biyometrik Analiz">
        <div className="text-center py-20">
          <p className="text-slate-500">Tarama bulunamadı</p>
          <Button onClick={() => navigate('/doctor/patients')} className="mt-4">
            Hastalar Sayfasına Dön
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout 
      title="Fetal Yüz Analizi" 
      subtitle={`${patient.first_name} ${patient.last_name}`}
    >
      <div className="space-y-6" data-testid="biometrik-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate(`/doctor/studio?scanId=${scanId}`)}
            className="text-slate-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Studio'ya Dön
          </Button>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              onClick={handleSaveMeasurements}
              disabled={saving}
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Kaydet
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={generating}
              className="bg-[#5A67D8] hover:bg-[#4F46E5]"
              data-testid="generate-fetal-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Üretiliyor...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Yüz Oluştur
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Measurements Form */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Ruler className="w-5 h-5 text-[#5A67D8]" />
                Biyometrik Ölçümler
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Gender Selection */}
              <div className="space-y-2">
                <Label>Cinsiyet</Label>
                <div className="flex gap-4">
                  <label className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    measurements.gender === 'female' 
                      ? 'border-[#5A67D8] bg-[#F8F7FC]' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}>
                    <input
                      type="radio"
                      name="gender"
                      value="female"
                      checked={measurements.gender === 'female'}
                      onChange={(e) => handleMeasurementChange('gender', e.target.value)}
                      className="w-4 h-4 text-[#5A67D8]"
                    />
                    <span className="text-pink-500 text-lg">♀</span>
                    <span>Kız</span>
                  </label>
                  <label className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    measurements.gender === 'male' 
                      ? 'border-[#5A67D8] bg-[#F8F7FC]' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}>
                    <input
                      type="radio"
                      name="gender"
                      value="male"
                      checked={measurements.gender === 'male'}
                      onChange={(e) => handleMeasurementChange('gender', e.target.value)}
                      className="w-4 h-4 text-[#5A67D8]"
                    />
                    <span className="text-blue-500 text-lg">♂</span>
                    <span>Erkek</span>
                  </label>
                </div>
              </div>

              {/* Measurements Grid */}
              <div className="grid grid-cols-2 gap-4">
                {MEASUREMENT_FIELDS.map((field) => (
                  <div key={field.key} className="space-y-1">
                    <Label htmlFor={field.key} className="text-xs">
                      {field.label}
                      <span className="text-slate-400 ml-1">({field.unit})</span>
                    </Label>
                    <Input
                      id={field.key}
                      type="number"
                      min={field.min}
                      max={field.max}
                      step="0.1"
                      value={measurements[field.key] || ''}
                      onChange={(e) => handleMeasurementChange(field.key, parseFloat(e.target.value) || '')}
                      placeholder={String(DEFAULT_MEASUREMENTS[field.key])}
                      className="h-9 bg-white"
                      data-testid={`measurement-${field.key}`}
                    />
                    <p className="text-xs text-slate-400">{field.description}</p>
                  </div>
                ))}
              </div>

              {/* Quick Info */}
              <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
                <p className="font-medium mb-1">💡 İpucu</p>
                <p>Ölçüleri girdikten sonra "Yüz Oluştur" butonuna tıklayarak fetal yüz görselini oluşturabilirsiniz.</p>
              </div>
            </CardContent>
          </Card>

          {/* Generated Image Preview */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Baby className="w-5 h-5 text-[#5A67D8]" />
                Fetal Yüz Önizleme
              </CardTitle>
            </CardHeader>
            <CardContent>
              {generatedImageUrl ? (
                <div className="space-y-4">
                  <div className="relative rounded-xl overflow-hidden bg-slate-100 aspect-square">
                    <img
                      src={generatedImageUrl}
                      alt="Fetal Yüz"
                      className="w-full h-full object-cover"
                      data-testid="fetal-face-preview"
                    />
                  </div>
                  <div className="flex items-center justify-between text-sm text-slate-500">
                    <span>Motor: Fetal Morph</span>
                    <span>Cinsiyet: {measurements.gender === 'female' ? 'Kız' : 'Erkek'}</span>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-24 h-24 rounded-full bg-[#E0D7FF] flex items-center justify-center mb-4">
                    <Baby className="w-12 h-12 text-[#5A67D8]" />
                  </div>
                  <p className="text-slate-500 mb-2">Henüz görsel üretilmedi</p>
                  <p className="text-sm text-slate-400">Ölçüleri girin ve "Yüz Oluştur" butonuna tıklayın</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Patient Info Card */}
        <Card className="border-0 shadow-lg bg-slate-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-[#5A67D8] flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-medium text-slate-800">
                  {patient.first_name} {patient.last_name}
                </p>
                <p className="text-sm text-slate-500">
                  {patient.pregnancy_formatted || (patient.pregnancy_week ? `${patient.pregnancy_week}. hafta` : '')}
                  {patient.phone && ` • ${patient.phone}`}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default BiometrikPage;
